import { useCallback, useContext, useMemo } from 'react'
import Fuse from 'fuse.js'
import { collection, documentId, query, where } from 'firebase/firestore'
import { useCollection } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { isActiveStatus } from '../../../common/access/productFeatureTags'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { Pattern } from '../../builder/contexts/PatternContext'
import { useStandardAlertTemplates } from './useStandardAlertTemplates'
import {
  CreateAlertFromTemplateResult,
  useCreateAlertFromTemplate,
} from './useCreateAlertFromTemplate'
import StandardAlertTemplate from '../types/StandardAlertTemplate'
import { captureEvent } from '../../../posthog'

// Firestore caps an `in` filter at 30 values — far above the handful of templates offered.
const MAX_PATTERN_IDS = 30

// A template plus the standard pattern it points at, so the card can title itself the way
// an alert card does. `pattern` is absent until it loads or if the pattern is gone.
export type TemplateCard = StandardAlertTemplate & { pattern?: Pattern }

type UseTemplateCardsOptions = {
  // The alerts query result, not the context's alerts state: that state is filled by an
  // effect a frame later, and ownership read from it would paint a card for an alert the
  // member already owns.
  alerts: Alert[]
  searchTerm: string
  // False while the alerts query is still settling, while the alerts tutorial runs (its
  // steps target the first card in the DOM), and in the admin templates view.
  enabled: boolean
  onAlertCreated: (alertId: string) => void
}

type ActivationFeedback = {
  severity: 'success' | 'info' | 'warning'
  message: string
  autoHideDuration: number
}

// What the member is told after a card's Activate click succeeds.
function activationFeedback(
  name: string,
  result: CreateAlertFromTemplateResult
): ActivationFeedback {
  const label = `"${name}"`

  if (result.outcome === 'exists') {
    return {
      severity: 'info',
      message: `${label} is already in your alerts`,
      autoHideDuration: 4000,
    }
  }

  if (result.active) {
    return { severity: 'success', message: `${label} is active`, autoHideDuration: 4000 }
  }

  if (result.inactiveReason === 'customIndicators') {
    return {
      severity: 'info',
      message: `${label} was added but left off — custom indicators can't run in alerts yet.`,
      autoHideDuration: 8000,
    }
  }

  return {
    severity: 'warning',
    message: `${label} was added but left off — you're at your active alert limit. Turn another alert off to activate it.`,
    autoHideDuration: 8000,
  }
}

/**
 * The standard alert templates a member doesn't have an alert for yet, plus the action
 * that creates one.
 *
 * Templates are rendered as cards instead of being provisioned in advance, so an admin
 * adding or removing a template changes what every member is offered immediately, and a
 * member who deletes their alert gets the card back.
 *
 * The listeners are live and tiny (single-digit docs), which is what makes that immediacy
 * work. Read access is granted to any signed-in user by the standardAlertTemplates and
 * standardPatterns rules; who is *offered* a template is decided here and enforced again
 * by the createAlertFromTemplate callable.
 */
export function useTemplateCards({
  alerts,
  searchTerm,
  enabled,
  onAlertCreated,
}: UseTemplateCardsOptions) {
  const { tradingHouse, usermeta } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const createAlert = useCreateAlertFromTemplate()

  // Same gate as the callable: an active membership in this trading house, or admin.
  const entitled = useMemo(
    () =>
      !!usermeta?.isAdmin ||
      isActiveStatus(usermeta?.tradingHouses?.[tradingHouse]?.currentStatus?.status),
    [usermeta, tradingHouse]
  )

  const offering = enabled && entitled

  // Gates the listener, not just its output: nobody who can't be shown a card subscribes.
  const { templates, loaded: templatesLoaded } = useStandardAlertTemplates({ enabled: offering })

  const offered = useMemo(
    () => (offering ? templates.filter((t) => t.autoProvision !== false) : []),
    [templates, offering]
  )

  // Only the patterns offered templates point at, in one bounded query. Keyed on every
  // offered template rather than the unowned ones, so creating an alert doesn't change the
  // query.
  const patternIds = useMemo(
    () =>
      Array.from(new Set(offered.map((t) => t.patternId).filter(Boolean)))
        .sort()
        .slice(0, MAX_PATTERN_IDS),
    [offered]
  )

  const patternsQuery = useMemo(
    () =>
      patternIds.length
        ? query(
            collection(firebase_firestore, `tradingHouses/${tradingHouse}/standardPatterns`),
            where(documentId(), 'in', patternIds)
          )
        : undefined,
    [tradingHouse, patternIds]
  )

  const [patternsSnapshot] = useCollection(patternsQuery)

  // The offer is on but a listener hasn't delivered yet. Judged by whether a snapshot has
  // arrived, not by the hooks' loading flags: those still read false for the render in which
  // a subscription starts, which is exactly when a member with no alerts would otherwise
  // flash "No Alerts found".
  const templateCardsLoading =
    offering && (!templatesLoaded || (!!patternsQuery && patternsSnapshot === undefined))

  const patternsById = useMemo(() => {
    const map = new Map<string, Pattern>()
    patternsSnapshot?.docs.forEach((d) => map.set(d.id, { ...(d.data() as Pattern), id: d.id }))
    return map
  }, [patternsSnapshot])

  const ownedTemplateIds = useMemo(
    () => new Set(alerts.map((alert) => alert.alertTemplateId).filter(Boolean) as string[]),
    [alerts]
  )

  // Held until both listeners have delivered, so a card never swaps its title in after paint.
  const unownedTemplates = useMemo<TemplateCard[]>(() => {
    if (templateCardsLoading) return []
    return offered
      .filter((template) => !ownedTemplateIds.has(template.id))
      .map((template) => ({ ...template, pattern: patternsById.get(template.patternId) }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [offered, ownedTemplateIds, patternsById, templateCardsLoading])

  // Same matcher and threshold the alert list uses — including the pattern name — so one
  // search term can't keep alerts on screen while silently dropping the templates.
  const fuse = useMemo(
    () =>
      new Fuse(unownedTemplates, {
        keys: ['name', 'symbol', 'pattern.name'],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [unownedTemplates]
  )

  const templateCards = useMemo(() => {
    const term = searchTerm.trim()
    if (!term) return unownedTemplates
    return fuse.search(term).map((result) => result.item)
  }, [unownedTemplates, fuse, searchTerm])

  // Resolves true once the member has the alert (created now or already there), so the
  // card can stay disabled until the alerts listener replaces it.
  const createFromTemplate = useCallback(
    async (template: StandardAlertTemplate): Promise<boolean> => {
      const result = await createAlert(template.id, tradingHouse)

      if (result.status !== 200) {
        setSnackbarSettings({
          severity: 'error',
          message: result.message || 'Could not activate the alert',
          autoHideDuration: 6000,
        })
        return false
      }

      // The card is replaced by the real alert card once the Firestore listener delivers
      // the new doc; parking the id scrolls to it when that happens.
      if (result.alertId) onAlertCreated(result.alertId)

      if (result.outcome !== 'exists') {
        captureEvent('alert_template_activated', {
          activated: result.active,
        })
      }

      setSnackbarSettings(activationFeedback(template.name, result))
      return true
    },
    [createAlert, tradingHouse, setSnackbarSettings, onAlertCreated]
  )

  return { templateCards, templateCardsLoading, createFromTemplate }
}
